import React, { createContext, useEffect, useReducer } from 'react';
import SplashScreen from 'src/components/SplashScreen';
import { PublicClientApplication } from '@azure/msal-browser';
import { getUserInfo } from 'src/utilities/msalUtilities';
import { msalConfig } from 'src/config';
import { clearAllCaches } from 'src/utilities/AppCaches';

const instance = (() => {
  if (typeof window !== 'undefined' && !window.__msalInstance) {
    window.__msalInstance = new PublicClientApplication(msalConfig);
  }
  return window.__msalInstance || new PublicClientApplication(msalConfig);
})();

const initialAuthState = {
  isAuthenticated: false,
  isInitialized: true,
  user: null,
  hasGroup: true,
  hasPermission: () => false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    default: {
      return { ...state, isInitialized: false };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  loginWithRedirect: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      if (!isMounted) return;

      const key = Object.keys(localStorage).filter((key) => localStorage.getItem(key).startsWith('{"failedRequests'))[0];
      const telemetryString = localStorage.getItem(key);
      var serverTelemetry = JSON.parse(telemetryString);

      if (serverTelemetry?.failedRequests.length > 0) {
        localStorage.clear();
      }

      const initialize = async () => {
        if (!isMounted || state.user) {
          if (isMounted) {
            dispatch({
              payload: {
                isAuthenticated: true,
              },
            });
          }
          return;
        }
        try {
          if (isMounted) {
            dispatch({
              type: 'INITIALISE',
              payload: {
                isAuthenticated: false,
                user: state.user,
              },
            });
          }
          await getUserInfo().then(async (response) => {
            if (isMounted) {
              await dispatch({
                type: 'LOGIN',
                payload: {
                  user: response,
                },
              });
            }
          });
        } catch (error) {
          if (isMounted) {
            await handleLogin();
          }
        }
      };
      await initialize();
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [state.user]);

  const handleLogin = async () => {
    try {
      await instance.initialize();

      const response = await instance.handleRedirectPromise();

      if (response && response.account) {
        instance.setActiveAccount(response.account);
        try {
          const userInfo = await getUserInfo();
          dispatch({
            type: 'LOGIN',
            payload: {
              user: userInfo,
            },
          });
        } catch (error) {
          console.warn('Failed to get user info, using account data:', error);
          dispatch({
            type: 'LOGIN',
            payload: {
              user: response.account,
            },
          });
        }
        return;
      }

      if (state.isInitialized) {
        if (!state.user) {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            await instance.loginRedirect();
          } else {
            instance.setActiveAccount(accounts[0]);
            try {
              const userInfo = await getUserInfo();
              dispatch({
                type: 'LOGIN',
                payload: {
                  user: userInfo,
                },
              });
            } catch (error) {
              console.warn('Failed to get user info, using account data:', error);
              dispatch({
                type: 'LOGIN',
                payload: {
                  user: accounts[0],
                },
              });
            }
          }
        }
        dispatch({
          payload: {
            isAuthenticated: true,
          },
        });
      }
    } catch (error) {
      console.error('Failed to initialize MSAL or handle login:', error);
      // If initialization fails, we might need to refresh or show an error
    }
  };

  const handleLogout = async () => {
    await instance.initialize();

    await instance.handleRedirectPromise();

    // Clear all application caches for security
    try {
      clearAllCaches();
    } catch (error) {
      console.warn('Failed to clear caches during logout:', error);
    }
    // Clear auth token from localStorage
    localStorage.removeItem('auth');

    await instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
    dispatch({
      type: 'LOGOUT',
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  };

  const hasPermission = (role) => {
    let hasPerm = false;

    if (state.user && state.user?.groups) {
      hasPerm = state.user.groups.indexOf(role) != -1 || state.user.groups.indexOf('Lantern Admin') != -1;
    }

    return hasPerm;
  };

  if (!state.user) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loginWithRedirect: () => handleLogin(),
        logout: () => handleLogout(),
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
